<template>
  <div
    v-loading="taskLoading"
    class="member-task flex-1 flex-column"
    :class="{ 'show-member': showReviewerList }"
    element-loading-text="正在保存打分，请稍后..."
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(0, 0, 0, 0.8)"
  >
    <div v-if="reviewer.reviewerId && showReviewerList" class="flex">
      <div class="member-tab">
        <div
          v-for="(item, index) in reviewerList"
          :key="item.reviewerId"
          class="member-item"
          :class="{ active: reviewer.reviewerId === item.reviewerId }"
          @click="changeReviewer(index)"
        >
          {{ item.expertName }}
        </div>
      </div>
      <div v-if="!reviewerSubmitted" class="flex">
        <el-button
          class="m-r-8 download-btn"
          type="text"
          @click="exportScoreTemplate"
        >
          导出评分模板
        </el-button>
        <upload-file @upload="importScoreTemplate">
          <template v-slot:default>
            <el-button type="text">
              导入评分模板
            </el-button>
          </template>
        </upload-file>
      </div>
    </div>
    <!-- 价格分评审开始评审前操作 -->
    <div v-if="showQuoteReviewSetting" class="quote-review-setting flex">
      <div>
        <el-button type="primary" @click="openQuoteRateDialog">
          设置企业折扣率
        </el-button>
        <el-button
          v-if="!quoteReviewOpened"
          type="primary"
          @click="openQuoteReview"
        >
          开启打分
        </el-button>
      </div>
      <div class="setting-alert m-l-16">
        <i class="el-icon-info"></i>
        <span>重要提示：</span>
        <span class="alert-item">1.价格分评分环节需要手动开启打分；</span>
        <span class="alert-item">2.开启打分前，需要确认最终企业折扣率；</span>
        <span class="alert-item">
          3.开启打分后，评审过程不能再支持修改企业折扣率，只能按照【开启打分】时折扣率进行评分。
        </span>
      </div>
    </div>
    <online-review-task
      ref="reviewTask"
      :key="reviewer.reviewerId"
      class="flex-1"
      :order-id="orderId"
      :purchase-type="purchaseType"
      :review-id="reviewId"
      :round-id="roundId"
      :review-way="reviewWay"
      :reviewer="reviewer"
      :review-closed="reviewClosed"
      :is-review-close="isReviewClose"
      :submitted="reviewerSubmitted"
      :offer-unit="offerUnit"
      :show-file-dropdown="showFileDropdown"
      :quote-review-opened="quoteReviewOpened"
      :offer-require-tax="offerRequireTax"
      @afterSubmitAll="nextReviewer"
      @reload="reload"
    />

    <online-review-quote-rate-dialog
      ref="quoteRateDialog"
      :order-id="orderId"
      :review-id="reviewId"
      :way-id="reviewWay.wayId"
      :purchase-type="purchaseType"
      :quote-review-opened="quoteReviewOpened"
      @afterSubmit="reloadBidderQuote"
    />
  </div>
</template>

<script>
import OnlineReviewTask from '@/components/online-review/review/OnlineReviewTask.vue'
import UploadFile from '@/components/common/UploadFile.vue'
import OnlineReviewQuoteRateDialog from '@/components/online-review/review/OnlineReviewQuoteRateDialog.vue'
import {
  exportPurchaseReviewScoreApi,
  getPurchaseReviewExpertScoreListApi,
  importPurchaseReviewScoreApi,
  startPurchaseReviewQuoteReviewApi,
  submitPurchaseReviewScoreApi
} from '@/api/procure/purchase-review'
import {
  inquiryReviewTypeDict,
  onlineReviewExpertScoreStatusDict,
  onlineReviewScoreStatusDict
} from '@/dictionary/project/online-review'
import { currencyTypeDict, purchaseTypeDict } from '@/dictionary/project/common'
import { getInquiryReviewScorePageApi } from '@/api/procure/inquiry-review'
import { arrToObj } from '@/utils/utils'

import { getNegotiationReviewScorePageApi } from '@/api/procure/negotiation-review'

/**
 * 多专家评审任务
 */
export default {
  name: 'OnlineReviewMemberTask',

  components: { OnlineReviewTask, UploadFile, OnlineReviewQuoteRateDialog },

  provide() {
    return {
      quoteRateChange: this.quoteRateChange
    }
  },

  props: {
    reviewId: {
      type: [String, Number],
      default: ''
    },

    orderId: {
      type: [String, Number],
      default: ''
    },

    roundId: {
      type: [String, Number],
      default: ''
    },

    reviewClosed: {
      type: Boolean,
      default: false
    },

    // 当前评审办法
    reviewWay: {
      type: Object,
      default: () => {}
    },

    // 评审专家列表
    reviewerList: {
      type: Array,
      default: () => []
    },

    offerUnit: {
      type: String,
      default: currencyTypeDict.YUAN.label
    },

    purchaseType: {
      type: [Number, String],
      default: purchaseTypeDict.INQUIRY.value
    },

    showFileDropdown: {
      type: Boolean,
      default: false
    },

    // 是否终止评审
    isReviewClose: {
      type: Boolean,
      default: false
    },

    // 是否开启打分
    quoteReviewOpened: {
      type: Boolean,
      default: false
    },

    offerRequireTax: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 当前评审的专家下标
      reviewerIndex: 0,

      // 专家提交打分列表
      reviewerSummaryList: [],

      taskLoading: false,

      // 报价折扣率变更次数（临时变量，只用于通知相应子组件刷新数据）
      quoteRateChange: { index: 0 }
    }
  },

  computed: {
    reviewer() {
      if (this.isQualificationWay) {
        return { reviewerId: 0 }
      }
      return this.reviewerList[this.reviewerIndex] || { reviewerId: 0 }
    },

    // 当前评审办法是初步评审
    isQualificationWay() {
      return (
        this.reviewWay?.wayType === inquiryReviewTypeDict.QUALIFICATION.value
      )
    },

    isQuotationReview() {
      return this.reviewWay?.wayType === inquiryReviewTypeDict.QUOTATION.value
    },

    reviewerSummaryInfo() {
      return (
        this.reviewerSummaryList.find(
          item => item.reviewerId === this.reviewer.reviewerId
        ) || {}
      )
    },

    // 当前专家已提交打分
    reviewerSubmitted() {
      return (
        this.reviewerSummaryInfo.summaryStatus ===
        onlineReviewExpertScoreStatusDict.COMPLETED.value || this.allSubmitted
      )
    },

    // 是否全部已提交
    allSubmitted() {
      if (this.reviewerSummaryList.length === 0) return false

      return this.reviewerSummaryList.every(
        item =>
          item.summaryStatus ===
          onlineReviewExpertScoreStatusDict.COMPLETED.value
      )
    },

    // 显示评审专家列表
    showReviewerList() {
      // 根据UAC-11076需求，隐藏价格分评审中的专家列表
      if (this.reviewWay?.wayType === inquiryReviewTypeDict.BUSINESS.value) {
        return true
      }
      return false
    },

    // 显示企业折扣率设置栏
    showQuoteReviewSetting() {
      return this.isQuotationReview && !this.reviewerSubmitted
    }
  },

  watch: {
    reviewWay: {
      handler(val) {
        if (val?.wayId) {
          this.getReviewExpertScoreList()
        }
      },
      immediate: true,
      deep: true
    },

    reviewerList: {
      handler(val) {
        if (val?.length > 0) this.reviewerIndex = 0
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    changeReviewer(index) {
      this.reviewerIndex = index
    },

    /**
     * 切换到下一个评委
     */
    nextReviewer() {
      this.getReviewExpertScoreList().then(() => {
        // 如果是价格分，则直接切换至汇总tab
        if (this.isQuotationReview) {
          this.$emit('afterSubmitAll')
        }
        const index = this.reviewerList.findIndex(
          item => item.reviewerId === this.reviewer.reviewerId
        )
        // 如果当前是最后一个评委，则不切换
        if (index < 0 || index === this.reviewerList.length - 1) {
          // 返回事件，切换至汇总tab
          this.$emit('afterSubmitAll')
        } else {
          // 否则，自动切换到下一个评委
          this.reviewerIndex++
        }
      })
    },

    /**
     * 导出打分表
     */
    exportScoreTemplate() {
      exportPurchaseReviewScoreApi(this.reviewId, {
        wayId: this.reviewWay.wayId
      })
    },

    /**
     * 导入打分表
     */
    async importScoreTemplate(file) {
      this.taskLoading = true
      try {
        const importRes = await importPurchaseReviewScoreApi(
          this.reviewId,
          { wayId: this.reviewWay.wayId },
          { file }
        )
        const importList = importRes.data || []
        const importMap = arrToObj(importList, 'expertName')

        const objApi = {
          [purchaseTypeDict.INQUIRY.value]: getInquiryReviewScorePageApi,
          [purchaseTypeDict.NEGOTIATION.value]: getNegotiationReviewScorePageApi
        }
        const supplierListRes = await objApi[this.purchaseType](this.reviewId, {
          isListing: 1,
          orderId: this.orderId,
          wayId: this.reviewWay.wayId
        })
        const supplierList = supplierListRes.data?.records || []
        const saveList = this.reviewerList
          .map(reviewer => {
            return {
              reviewerId: reviewer.reviewerId,
              data: supplierList
                .map(supplier => {
                  const reviewerImport = importMap[reviewer.expertName]
                  const reviewerImportScoreMap = arrToObj(
                    (reviewerImport.scoreItems || []).filter(
                      item => item.supplierId === supplier.supplierId
                    ),
                    'itemName'
                  )
                  return {
                    wayId: this.reviewWay.wayId,
                    supplierId: supplier.supplierId,
                    supplierName: supplier.supplierName,
                    itemScoreList: this.reviewWay.itemInfoList
                      .map(item => {
                        const itemScore =
                          reviewerImportScoreMap[item.itemName] || {}
                        let score = itemScore.score
                        if (Number(itemScore.score) > Number(item.maxScore)) {
                          score = item.maxScore
                        }
                        if (Number(itemScore.score) < Number(item.minScore)) {
                          score = item.minScore
                        }
                        return {
                          wayItemId: item.wayItemId,
                          score: score,
                          remark: itemScore.remark
                        }
                      })
                      .filter(item => item.score)
                  }
                })
                .filter(supplier => supplier.itemScoreList?.length > 0)
            }
          })
          .flat()

        const requestList = saveList
          .map(item => {
            return item.data.map(requestData => {
              return submitPurchaseReviewScoreApi(this.reviewId, requestData, {
                reviewerId: item.reviewerId
              })
            })
          })
          .flat()
        await Promise.allSettled(requestList)
      } catch (err) {
        if (err?.message) this.$message.error(err.message)
      } finally {
        this.taskLoading = false
      }
    },

    /**
     * 获取当前评审办法下专家的打分情况
     */
    getReviewExpertScoreList() {
      return getPurchaseReviewExpertScoreListApi(this.reviewId, {
        wayId: this.reviewWay.wayId
      })
        .then(res => {
          this.reviewerSummaryList = res.data || []
          this.$emit('getAllSubmitted', this.allSubmitted)
        })
        .catch(err => {
          console.error('getReviewExpertScoreList: ', err)
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 打开企业折扣率设置弹窗
     */
    openQuoteRateDialog() {
      this.$refs.quoteRateDialog.openDialog()
    },

    reloadBidderQuote() {
      this.quoteRateChange.index += 1
    },

    /**
     * 开启价格分评审
     */
    openQuoteReview() {
      this.$systemConfirm(h => (
        <div>
          开启打分后，
          <span class='text-danger'>评审过程中不支持修改企业折扣率</span>
          ，请先检查企业折扣率是否设置完整。
        </div>
      )).then(bool => {
        if (!bool) return

        startPurchaseReviewQuoteReviewApi(this.reviewId)
          .then(res => {
            this.$emit('reload')
          })
          .catch(err => {
            if (err?.message) this.$message.error(err.message)
          })
      })
    },

    reload() {
      this.$emit('reload')
    }
  }
}
</script>

<style scoped lang="scss">
.member-task {
  overflow: hidden;
}

.member-tab {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 8px 24px;
  margin-top: -8px;

  .member-item {
    margin-top: 8px;
    min-width: 80px;
    min-height: 32px;
    padding: 0 8px;
    text-align: center;
    line-height: 32px;
    border: 1px solid $info-fourth-color;
    color: $second-text-color;
    cursor: pointer;

    &.active {
      color: $white-color;
      background: $primary-color;
    }

    &:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    &:last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
}

.quote-review-setting {
  padding: 16px 24px;
  border-bottom: 1px solid $info-fourth-color;

  .setting-alert {
    border: 1px solid $primary-second-color;
    background: $primary-fourth-color;
    color: $third-text-color;
    font-size: 12px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    border-radius: 4px;

    i {
      font-size: 18px;
      color: $primary-color;
      margin-right: 8px;
    }

    .alert-item {
      color: $text-color;
      font-size: 14px;
    }
  }
}
</style>
